import { API_BASE } from "./api";
import { STORAGE_KEYS } from "./storageKeys";
import { log } from "./log";
import { describeOktaError } from "./authErrors";
import { jwtExpiresAt } from "./sessionExpiry";

// Client side of Okta sign-in for the SPA: OAuth 2.0 Authorization Code + PKCE run
// entirely in the browser against Okta (a public client). The hub publishes its
// own issuer + client_id at GET /api/auth/config; we discover Okta's endpoints,
// redirect to Okta, exchange the returned code for tokens, and cache them in
// localStorage. Every API call sends the (kept-fresh) ID token as a Bearer.
//
// getToken() is intentionally SYNCHRONOUS so the api client and EventSource URLs
// stay simple — AuthGate refreshes the token in the background via ensureFreshToken().

const KEY = STORAGE_KEYS.authToken;
// `groups` carries the Okta group memberships the server's admin gate reads
// (core/admin.ts); the Okta app's groups-claim filter decides which groups land
// in the ID token.
const SCOPE = "openid profile email groups offline_access";
// Okta requires redirect_uri to EXACTLY match a registered Login redirect URI.
// Pin it to the origin root — NOT the current route — so a single registered
// URI (e.g. https://omnihub.incode.com/) works no matter which page the
// user was on when they clicked sign in. Using window.location.pathname here
// would send a deep-link path Okta doesn't know, and it rejects the request:
// "The 'redirect_uri' parameter must be a Login redirect URI in the client app
// settings." The same constant is used for both the authorize redirect and the
// token exchange, so the two always agree.
const REDIRECT_URI = window.location.origin + "/";
const PKCE_VERIFIER = "omni_hub_pkce_verifier";
const PKCE_STATE = "omni_hub_pkce_state";

export interface AuthConfig {
  enabled: boolean;
  issuer: string | null;
  clientId: string | null;
}
interface Stored {
  idToken: string;
  expiresAt: number;
  issuer: string;
  clientId: string;
  tokenEndpoint: string;
}
interface OidcMeta {
  authorization_endpoint: string;
  token_endpoint: string;
}

let _config: AuthConfig | null = null;

// SECURITY (defense-in-depth): the long-lived REFRESH token is kept in memory
// only — never persisted to localStorage. A stored refresh token is a durable,
// high-value credential that an XSS or a shared/compromised machine could lift
// and replay indefinitely. Holding it in a module variable ties its lifetime to
// the tab: a page reload drops it, so once the (short-lived, still-persisted) id
// token expires the user re-authenticates via Okta — usually a quick redirect
// thanks to Okta's own SSO session. The id token stays in localStorage so
// getToken() remains synchronous and a reload keeps the user signed in until
// that token's normal expiry.
let refreshTokenMem: string | null = null;

/** Read the hub's published Okta config (cached for the session).
 *
 * SECURITY — fail CLOSED: only an explicit `enabled: false` from the server
 * opens the local (name-yourself) gate. If the config can't be fetched at all,
 * we report auth as enabled-but-unconfigured (issuer null) so AuthGate shows
 * the sign-in screen instead of dropping into the open local mode. Failures
 * are NOT cached, so a retry/reload re-fetches. */
export async function fetchAuthConfig(): Promise<AuthConfig> {
  if (_config) return _config;
  try {
    const r = await fetch(`${API_BASE}/api/auth/config`);
    if (r.ok) {
      _config = (await r.json()) as AuthConfig;
      return _config;
    }
  } catch {
    /* fall through to the fail-closed sentinel below */
  }
  return { enabled: true, issuer: null, clientId: null };
}

/** Whether the hub actually has Okta sign-in configured (an issuer + client id).
 * Reads the config cached by fetchAuthConfig(): false until that first fetch
 * resolves, and false in off-Okta local dev (the open gate reports
 * `enabled: false`). apiFetch and AuthGate use it to decide whether a 401 — or a
 * token that has since expired to null — should bounce back to the Okta sign-in
 * screen. In open dev there is never a token to expire, so it must NOT. */
export function isOktaConfigured(): boolean {
  return Boolean(_config?.enabled && _config.issuer && _config.clientId);
}

function read(): Stored | null {
  try {
    return JSON.parse(localStorage.getItem(KEY) || "") as Stored;
  } catch {
    return null;
  }
}
function write(s: Stored): void {
  localStorage.setItem(KEY, JSON.stringify(s));
}
/**
 * When a newly-issued ID token expires: its own `exp` where we can read it,
 * falling back to the endpoint's `expires_in`.
 *
 * The fallback is load-bearing, not defensive noise. `sessionExpiresAt()` of null
 * means "there is no session" — off-Okta local dev — and sessionPhase reads that
 * as phase `none`, which shows no warning at all. So an undecodable token must
 * degrade to the old, approximate clock rather than propagate a null that would
 * silently switch the expiry warning off and reproduce ENG-54731 exactly.
 */
function expiryOf(idToken: string, expiresIn: number | undefined): number {
  return jwtExpiresAt(idToken) ?? Date.now() + (expiresIn ?? 3600) * 1000;
}

export function clearToken(): void {
  localStorage.removeItem(KEY);
  refreshTokenMem = null;
}

/** The current ID token (synchronous). AuthGate keeps it fresh in the background.
 * An EXPIRED stored token counts as signed out — returning it would let the
 * signed-out app chrome render and then 401 on every call. */
export function getToken(): string | null {
  const s = read();
  if (!s || Date.now() >= s.expiresAt) return null;
  return s.idToken;
}

/**
 * When the current session expires, in epoch ms — or null when there is no
 * session to expire (off-Okta, or signed out).
 *
 * Distinct from getToken(), which returns null for an EXPIRED token: the expiry
 * warning has to know about a session that is nearly over, and about one that has
 * just ended, and getToken() cannot tell those from "never signed in".
 */
export function sessionExpiresAt(): number | null {
  return read()?.expiresAt ?? null;
}

/**
 * Whether the session can be extended without leaving the page.
 *
 * False after a reload, and that is not a bug to route around: the refresh token
 * is deliberately memory-only (see refreshTokenMem), so a reloaded tab has no way
 * to renew silently and the honest answer is a reauth redirect. The expiry warning
 * uses this to label its button — "Extend session" when it can be done in place,
 * "Sign in again" when it cannot — because a button that says Extend and then
 * navigates away is the kind of surprise this ticket is about.
 */
export function canExtendSilently(): boolean {
  return !!refreshTokenMem && !!read();
}

/**
 * Try to extend the session in place. Resolves true only when the expiry actually
 * MOVED — a refresh that quietly failed (Okta declined, network down) must not
 * report success and leave the user believing they have another hour.
 */
export async function extendSession(): Promise<boolean> {
  const before = sessionExpiresAt();
  await ensureFreshToken({ force: true });
  const after = sessionExpiresAt();
  return after !== null && before !== null && after > before;
}

// ── Where to come back to after reauthenticating ──────────────────────────────
// REDIRECT_URI is pinned to the origin root because Okta only accepts registered
// redirect URIs, so Okta cannot bring anyone back to a deep link. The path is
// therefore parked here across the round trip — sessionStorage, like the PKCE
// verifier and state above, so it is tab-scoped and dies with the tab.
//
// Without this, reauthenticating mid-edit returned the writer to the app root:
// their draft was safe on the server and they were nowhere near it (ENG-54731).
const RETURN_TO = "omni_hub_return_to";

/** Remember the current location (path + query) to return to after reauth. */
export function rememberReturnTo(): void {
  try {
    sessionStorage.setItem(RETURN_TO, window.location.pathname + window.location.search);
  } catch {
    /* private mode / storage disabled — the reauth still works, it just lands at the root */
  }
}

/**
 * Take the remembered location, clearing it. Only ever an in-app path: a stored
 * value is read back as a PATH and rejected unless it starts with a single "/",
 * so nothing here can become an open redirect even if the storage were tampered
 * with.
 */
export function takeReturnTo(): string | null {
  try {
    const value = sessionStorage.getItem(RETURN_TO);
    sessionStorage.removeItem(RETURN_TO);
    if (!value || !value.startsWith("/") || value.startsWith("//")) return null;
    return value;
  } catch {
    return null;
  }
}

// ── Leaving deliberately, to come straight back ──────────────────────────────
// A reauth redirect is a real browser unload, so every `beforeunload` guard in
// the app fires and the browser asks "leave site?" — stalling the one navigation
// that can restore the session, on a dialog nobody is necessarily at the keyboard
// to answer. This flag lets those guards tell OUR redirect from a user closing
// the tab.
//
// Set only by reauthenticate() below, which callers reach only after flushing the
// work in hand — so standing the guard down is safe rather than merely convenient.
let reauthing = false;

/** Whether the app is in the middle of sending the user to Okta and back. */
export function isReauthInProgress(): boolean {
  return reauthing;
}

/**
 * Send the user to Okta to sign in again, returning them to the page they are on.
 *
 * Callers MUST have flushed unsaved work first — this navigates away, and the
 * unload guards stand down for it.
 */
export async function reauthenticate(): Promise<void> {
  // Off-Okta login() returns without navigating, and a flag raised for a redirect
  // that never comes would silently disarm the unload guards for the rest of the
  // page's life. Check before raising it rather than after — the config is cached,
  // so this costs nothing.
  const cfg = await fetchAuthConfig();
  if (!cfg.enabled) return;
  reauthing = true;
  try {
    await login();
  } catch {
    // Discovery or the network failed, so the redirect never happened: the user
    // is still on the page, and a tab close is theirs again, not ours.
    reauthing = false;
  }
}

// Fired when an API call comes back 401 despite a Bearer token — the server no
// longer accepts our session. AuthGate listens and drops back to the sign-in
// screen (unmounting the app and its data/SSE loops).
export const AUTH_EXPIRED_EVENT = "omni-hub:auth-expired";
export function notifyAuthExpired(): void {
  clearToken();
  window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
}

// ── PKCE helpers ──────────────────────────────────────────────────────────────
function b64url(bytes: ArrayBuffer | Uint8Array): string {
  const arr = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let s = "";
  for (const b of arr) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
function randomString(): string {
  return b64url(crypto.getRandomValues(new Uint8Array(32)));
}
async function challenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return b64url(digest);
}
async function discover(issuer: string): Promise<OidcMeta> {
  const r = await fetch(`${issuer.replace(/\/+$/, "")}/.well-known/openid-configuration`);
  if (!r.ok) throw new Error("OIDC discovery failed");
  return (await r.json()) as OidcMeta;
}

/** Begin sign-in: full-page redirect to Okta. */
export async function login(): Promise<void> {
  const cfg = await fetchAuthConfig();
  if (!cfg.enabled || !cfg.issuer || !cfg.clientId) return;
  // Okta can only return to a registered redirect URI (the origin root), so the
  // page the user was on rides across the round trip in sessionStorage.
  rememberReturnTo();
  const meta = await discover(cfg.issuer);
  const verifier = randomString();
  const state = randomString();
  sessionStorage.setItem(PKCE_VERIFIER, verifier);
  sessionStorage.setItem(PKCE_STATE, state);
  const url = new URL(meta.authorization_endpoint);
  url.search = new URLSearchParams({
    client_id: cfg.clientId,
    response_type: "code",
    scope: SCOPE,
    redirect_uri: REDIRECT_URI,
    state,
    code_challenge: await challenge(verifier),
    code_challenge_method: "S256",
  }).toString();
  window.location.href = url.toString();
}

/** Outcome of processing an Okta redirect. */
export interface CallbackResult {
  /** True when a token was successfully obtained and cached. */
  ok: boolean;
  /**
   * A human-readable Okta error to surface to the user (e.g. the account is not
   * assigned to the client app), or null when there was nothing to report — no
   * redirect in progress, or a clean success.
   */
  error: string | null;
}

/** On app load: if returning from Okta (?code / ?error), process it. */
export async function handleRedirectCallback(): Promise<CallbackResult> {
  const params = new URLSearchParams(window.location.search);
  const code = params.get("code");
  const state = params.get("state");
  const errorCode = params.get("error");

  // Okta bounced back with an error instead of a code (e.g. the user isn't
  // assigned to the client app). Clean the URL, drop the PKCE session, and
  // surface a friendly message so the UI can explain it instead of silently
  // looping back to the sign-in button.
  if (errorCode) {
    window.history.replaceState(null, "", window.location.pathname);
    sessionStorage.removeItem(PKCE_VERIFIER);
    sessionStorage.removeItem(PKCE_STATE);
    const message = describeOktaError(errorCode, params.get("error_description"));
    log.warn(`Okta authorization error: ${errorCode} — ${params.get("error_description") ?? ""}`);
    return { ok: false, error: message };
  }

  if (!code) return { ok: false, error: null };
  const cfg = await fetchAuthConfig();
  const verifier = sessionStorage.getItem(PKCE_VERIFIER);
  const expectedState = sessionStorage.getItem(PKCE_STATE);
  // Strip the OAuth params from the URL regardless of outcome.
  window.history.replaceState(null, "", window.location.pathname);
  sessionStorage.removeItem(PKCE_VERIFIER);
  sessionStorage.removeItem(PKCE_STATE);
  if (!cfg.issuer || !cfg.clientId || !verifier || state !== expectedState) {
    return { ok: false, error: null };
  }
  try {
    const meta = await discover(cfg.issuer);
    const r = await fetch(meta.token_endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        grant_type: "authorization_code",
        client_id: cfg.clientId,
        redirect_uri: REDIRECT_URI,
        code,
        code_verifier: verifier,
      }).toString(),
    });
    if (!r.ok) {
      log.warn(`Okta token exchange failed (${r.status})`);
      return { ok: false, error: "Sign-in couldn't be completed. Please try again." };
    }
    const tok = (await r.json()) as { id_token?: string; refresh_token?: string; expires_in?: number };
    if (!tok.id_token) {
      log.warn("Okta token exchange returned no id_token");
      return { ok: false, error: "Sign-in couldn't be completed. Please try again." };
    }
    // Refresh token lives in memory only (see refreshTokenMem); never persisted.
    refreshTokenMem = tok.refresh_token ?? null;
    write({
      idToken: tok.id_token,
      expiresAt: expiryOf(tok.id_token, tok.expires_in),
      issuer: cfg.issuer,
      clientId: cfg.clientId,
      tokenEndpoint: meta.token_endpoint,
    });
    return { ok: true, error: null };
  } catch (err) {
    log.warn("Okta auth callback failed:", err);
    return { ok: false, error: "Sign-in couldn't be completed. Please try again." };
  }
}

/** Refresh the cached token when near expiry (AuthGate calls this periodically).
 *
 *  `force` skips the not-near-expiry early return, for the expiry warning's
 *  "Extend session" button: that is a deliberate human request to renew now, and
 *  the 2-minute window it would otherwise wait for is longer than the button's
 *  own patience. */
export async function ensureFreshToken(opts: { force?: boolean } = {}): Promise<void> {
  const s = read();
  // No in-memory refresh token (fresh sign-in this tab, or lost across a reload)
  // ⇒ nothing to refresh; the id token rides until expiry, then re-auth via Okta.
  if (!s || !refreshTokenMem) return;
  if (!opts.force && Date.now() < s.expiresAt - 120_000) return;
  try {
    const r = await fetch(s.tokenEndpoint, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        grant_type: "refresh_token",
        client_id: s.clientId,
        refresh_token: refreshTokenMem,
        scope: SCOPE,
      }).toString(),
    });
    if (!r.ok) return;
    const tok = (await r.json()) as { id_token?: string; refresh_token?: string; expires_in?: number };
    if (!tok.id_token) return;
    // Okta may rotate the refresh token; keep the newest in memory only.
    if (tok.refresh_token) refreshTokenMem = tok.refresh_token;
    write({
      ...s,
      idToken: tok.id_token,
      expiresAt: expiryOf(tok.id_token, tok.expires_in),
    });
  } catch {
    /* keep the old token; a 401 will route the user back to sign-in */
  }
}

// ── Default Omni Hub sign-in (off-Okta) ───────────────────────────────────────
// When Okta is not configured the gate is open, but rather than silently using a
// generic stand-in we let the user name themselves. The chosen identity lives in
// localStorage and rides on every API call as X-Omni-User / X-Omni-Email headers
// (see api.ts), so the server resolves the same person for the user box + presence.
const LOCAL_KEY = STORAGE_KEYS.localUser;

export interface LocalUser {
  name: string;
  email: string;
}

export function getLocalUser(): LocalUser | null {
  try {
    const u = JSON.parse(localStorage.getItem(LOCAL_KEY) || "") as LocalUser;
    return u && u.name ? u : null;
  } catch {
    return null;
  }
}
export function setLocalUser(u: LocalUser): void {
  localStorage.setItem(LOCAL_KEY, JSON.stringify({ name: u.name.trim(), email: u.email.trim() }));
}
export function clearLocalUser(): void {
  localStorage.removeItem(LOCAL_KEY);
}

/** Sign out: drop the cached Okta token and the local identity, then reload. */
export function logout(): void {
  clearToken();
  clearLocalUser();
  window.location.reload();
}
